import React, { useCallback, useRef } from 'react';
import { View } from 'react-native';
import { useScrollContext } from '../contexts/scrollContext';

/**
 * Custom hook that manages cross-platform scrolling to elements using refs.
 * Uses measureLayout which works consistently across web and native platforms.
 */
export const useScrollToRef = (): {
  getRef: (id: string) => React.RefObject<View>;
  scrollToRef: (id: string) => void;
  containerRef: React.RefObject<View>;
} => {
  const { scrollTo } = useScrollContext();
  /** Tracks each element ref for scroll targeting */
  const refs = useRef<Record<string, React.RefObject<View>>>({});
  /** Coordinate reference for measuring positions */
  const containerRef = useRef<View>(null);

  /**
   * Factory function that creates and caches refs for each element.
   * Prevents unnecessary ref recreation on re-renders.
   */
  const getRef = useCallback((id: string): React.RefObject<View> => {
    let ref = refs.current[id];
    if (ref == null) {
      ref = React.createRef<View>();
      refs.current[id] = ref;
    }
    return ref;
  }, []);

  /**
   * Scrolls to an element using cross-platform measureLayout.
   * Works on both web and native by measuring element position relative to container.
   */
  const scrollToRef = useCallback(
    (id: string): void => {
      const elementRef = refs.current[id];
      if (elementRef?.current != null && containerRef.current != null) {
        /** Get element position relative to container */
        elementRef.current.measureLayout(
          containerRef.current,
          (_xOffset, yOffset) => {
            // -20 pixel offset to y helps lineup scrolling in a pleasing manner
            scrollTo({ yCoordinate: Math.max(0, yOffset - 20), animated: true });
          },
          () => {}, // Silent fallback
        );
      }
    },
    [scrollTo],
  );

  return { getRef, scrollToRef, containerRef };
};
